/**
 * @name useEmpLogic.js
 * @author 김권영
 * @since 2025. 02. 27.
 * @description
 * 사업장(관리자 조회), 공정별 설비의 재해 관련 직원 매핑 관리 로직을 담당하는 React 훅
 * - 공정별 설비 목록 조회
 * - 관련 직원 목록 조회
 * - 선택 후 매핑
 * 
 * @param {string} compCode - 관리자 : 선택된 사업장 코드(전역), 유저 : 로그인 정보에 담긴 사업장 코드
 * @returns {Object} - 함수와 상태를 반환
 */
import { useState, useEffect, useCallback } from "react";
import { get, post } from "@utils/axios_api";
import { toast } from 'react-toastify';
export function useSupvLogic(compCode, siteCode) {
  // 회사 정보와 부서 목록
    const [company, setCompany] = useState([]);
    const [site, setSite] = useState([]);
    const [departmentList, setDepartmentList] = useState([]);

  // 가운데(설비작업관련직원)와 오른쪽(직원목록), 선택된 공정
  const [centerEmployeeList, setCenterEmployeeList] = useState([]);
  const [rightEmployeeList, setRightEmployeeList] = useState([]);
  const [selectedProcEqpt, setSelectedProcEqpt] = useState(null);

  // [초기 데이터] 회사목록, 부서목록 (공통)
  const fetchInitialData = useCallback(async () => {
    try {
      const data = await get("/user/supv", {
        params: { siteCode: siteCode, compCode: compCode },
      });
      setDepartmentList(data.departmentList || []);
    } catch (err) {
      console.error(err);
    }
  }, [siteCode, compCode]);

  // siteCode 변경 시, 가운데/오른쪽/선택 공정 초기화
  useEffect(() => {
    setSelectedProcEqpt(null);
    setCenterEmployeeList([]);
    setRightEmployeeList([]);
  }, [siteCode]);

  /**
   * 모든 사업장 직원 매핑 데이터를 저장하는 함수.
   * @param {string} siteCodeParam -  사업장 코드.
   * @param {int} mpreqCodeParam - 선택된 공정별 설비 코드.
   * @param {string} deptCodeParam - 부서 코드 (옵션).
   * @param {string} searchWrd - 검색어 (옵션).
   */
  async function handleSaveAll(siteCodeParam, mpreqCodeParam, deptCodeParam, searchWrd){
    if (!mpreqCodeParam) {
      toast.warn("공정(설비)를 먼저 선택하세요.");
      return;
    }
    if (!window.confirm("저장 하시겠습니까?")) return;
    try {
      const payloadSupv = centerEmployeeList.map((r) => ({
        mpesuCode: r.mpesuCode ?? 0,
        mpreqCode: mpreqCodeParam,
        empCode: r.empCode,
      }));
      const response = await post("/user/supvSave", {
        payloadSupv,
        mpreqCode: mpreqCodeParam,
      });
      toast.success(response.message);
      // 저장 후 가운데 데이터 재조회
      await fetchCenterEmployeeList(siteCodeParam, mpreqCodeParam);
      if (deptCodeParam || searchWrd) {
        await fetchRightEmployeeList({ siteCodeParam, mpreqCodeParam, deptCodeParam, searchWrd });
      }
    } catch (err) {
      console.error("저장 오류:", err);
    }
  }

  /**
   * 가운데 테이블(설비 작업 관련 직원 매핑)을 조회하는 함수.
   * @param {string} siteCodeParam - 사업장 코드.
   * @param {int} mpreqCodeParam - 선택된 공정별 설비 코드.
   */
  async function fetchCenterEmployeeList(siteCodeParam, mpreqCodeParam) {
    try {
      const data = await get("/user/supvMapping", {
        params: { siteCode: siteCodeParam,
            compCode: compCode,
            mpreqCode: mpreqCodeParam },
      });
      setCenterEmployeeList(data.supvMappingList || []);
    } catch (err) {
      console.error("가운데 테이블 조회 오류:", err);
    }
  }

  /**
   * 오른쪽 테이블(직원 목록)을 조회하는 함수.
   * @param {string} siteCodeParam - 회사 코드.
   * @param {int} mpreqCodeParam - 선택된 공정별 설비 코드.
   * @param {string} deptCodeParam - 부서 코드 (옵션).
   * @param {string} searchWrd - 검색어 (옵션).
   */
  async function fetchRightEmployeeList({ siteCodeParam, mpreqCodeParam, deptCodeParam, searchWrd }) {
    try {
      const data = await get("/user/supvList", {
        params: {
            siteCode: siteCodeParam,
            compCode: compCode,
            mpreqCode: mpreqCodeParam || 0, 
            deptCode: deptCodeParam || "",
            searchWrd: searchWrd || "" 
          },
      });
      setRightEmployeeList(data.supvList || []);
    } catch (err) {
      console.error("오른쪽 테이블 조회 오류:", err);
    }
  }

  /**
   * 가운데 테이블 행 클릭 시 선택 상태를 토글하는 함수.
   * @param {int} empCode - 클릭한 직원의 코드.
   */
  function handleCenterRowClick(empCode) {
    setCenterEmployeeList((prev) =>
      prev.map((r) =>
        r.empCode === empCode ? { ...r, isChecked: !r.isChecked } : r
      )
    );
  }
  /**
   * 오른쪽 테이블 행 클릭 시 선택 상태를 토글하는 함수.
   * @param {int} empCode - 클릭한 직원의 코드.
   */
  function handleRightRowClick(empCode) {
    setRightEmployeeList((prev) =>
      prev.map((r) =>
        r.empCode === empCode ? { ...r, isChecked: !r.isChecked } : r
      )
    );
  }

  // 오른쪽 테이블에서 선택된 직원들을 가운데 테이블로 이동 시키는 함수 
  function moveRightToCenter() {
    const selected = rightEmployeeList.filter((r) => r.isChecked);
    if (selected.length === 0) return;
    const itemsToCenter = selected.map((it) => ({ ...it, isChecked: false }));
    setCenterEmployeeList((prev) => [...prev, ...itemsToCenter]);
    setRightEmployeeList((prev) => prev.filter((r) => !r.isChecked));
  }

  // 가운데 테이블에서 선택된 직원들을 가운데 테이블로 이동 시키는 함수
  function moveCenterToRight() {
    const selected = centerEmployeeList.filter((r) => r.isChecked);
    if (selected.length === 0) return;
    const itemsToRight = selected.map((it) => ({ ...it, isChecked: false }));
    setRightEmployeeList((prev) => [...prev, ...itemsToRight]);
    setCenterEmployeeList((prev) => prev.filter((r) => !r.isChecked));
  }

  return {
    site, company, departmentList, centerEmployeeList, rightEmployeeList,
    selectedProcEqpt,
    setSelectedProcEqpt, setCenterEmployeeList, setRightEmployeeList,
    fetchInitialData, fetchCenterEmployeeList, fetchRightEmployeeList,
    handleSaveAll, handleCenterRowClick, handleRightRowClick,
    moveRightToCenter, moveCenterToRight,
  };
}
